/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Bønnetider: the year's timetable, this week, overrides and the Hijri date.
 * CA-20, CA-21, CA-22, CA-41.
 *
 * Rules carried over from the prototype, each one a fault it had:
 *  - The runway note is THE number here, from the API, never typed (it once
 *    read "116 dager" forever; then "undefined dager" from a field that did
 *    not exist).
 *  - A row says which prayer was overridden, not only that the day was, and
 *    `source === 'override'` — not "a time differs" — decides Fjern vs Endre,
 *    so a note-only override can be seen and removed (fault 110).
 *  - An override changes only the fields a person actually changed; the note
 *    already on a day is loaded (in Norwegian only, fault 111) so editing does
 *    not wipe it; any change after a preview voids the preview.
 *  - The upload is previewed in plain words before anything is written; big
 *    jumps must be acknowledged; days with their own changes are kept unless
 *    somebody chooses otherwise.
 *  - The Hijri reason is SHOWN TO MEMBERS, and the form says so where it is
 *    typed.
 *  - Jamaat times are periods (Jamaat.tsx, 2026-10-15); the week shows what they come to, as the app does.
 *  - «Fjern» on an override and «Importer» on a year are two clicks whose second says what changes (QA A13,
 *    2026-10-16); an override's «Årsak» starts EMPTY and must be chosen — it defaulted to «Eid», which members then
 *    read on every changed day (QA A15).
 */
import React, { useEffect, useRef, useState } from 'react';
import { can, errText, post, readUpload, saveBlob, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { longDate, osloTime, shortDate, isoToday } from '../lib/format';
import { templateCsv } from '../lib/timetable';
import { Body as CardBody, Btn, Card, DropZone, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { TimetableSource } from './TimetableSource';
import { JamaatCard } from './Jamaat';
import { sourceNote, templateWhere, type AdoptionState } from '../lib/timetable-card-text';

const PRAYERS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
const cap = (s: string) => s[0].toUpperCase() + s.slice(1);

export function Times() {
  const { data, error, reload } = useLoad<Body>('/prayer-times');
  const [editing, setEditing] = useState<{ date: string; seq: number } | null>(null);
  const [ovNote, sayOv] = useMsg();
  const { caps } = useNav();
  const sourceOn = Boolean(caps && caps.includes('timetable_source'));
  // The source section only where the mosque HAS the capability and this person may read it; the server decides the rest.
  const hasSource = Boolean(caps && caps.includes('timetable_source')) && can('timetable.source.read');
  // Where the mosque stands with adoption, as the source section last said — the old card words its note by it.
  const [adoption, setAdoption] = useState<AdoptionState | null>(null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const rows: Body[] = data.items || [];
  return (
    <>
      {hasSource && <TimetableSource onPublished={reload} onAdoption={setAdoption} />}
      {/* Versions close only the direct import. The old card remains for its year list and template download. */}
      <TimetableCard data={data} onImported={reload} sourceOn={sourceOn} hasSource={hasSource} adoption={adoption} />
      <Card title="Denne uken" hint="Stor tid = når bønnen begynner, jamaat under. Gule rader er overstyrt for én dag — de kommer ikke fra årstabellen.">
        <table className="t">
          <thead><tr><th scope="col">Dato</th>{PRAYERS.map((p) => <th scope="col" key={p}>{cap(p)}</th>)}<th scope="col"><span className="vh">Handling</span></th></tr></thead>
          <tbody>
            {rows.length ? rows.map((r) => {
              const over = r.source === 'override';
              const changed: string[] = r.overriddenFields || [];
              return (
                <tr key={r.date} className={over ? 'over' : undefined}>
                  <td>{shortDate(r.date)}{' '}
                    {r.date === data.today && <Tag tone="green">i dag</Tag>}{' '}
                    {changed.length > 0 && <Tag tone="gold">overstyrt</Tag>}{' '}
                    {r.isClosed && <Tag tone="red">stengt</Tag>}{' '}
                    {r.overrideNote && <Tag tone="gold" title={r.overrideNote}>notat: {r.overrideNote}</Tag>}{' '}
                    {r.jamaat && r.jamaat.jumuaJamaat && <Tag tone="green">jumu'ah {r.jamaat.jumuaJamaat}</Tag>}
                  </td>
                  {PRAYERS.map((k) => (
                    <td key={k} style={changed.includes(k) ? { color: 'var(--gold-text)', fontWeight: 600 } : undefined}>
                      {/* Start time first: a timetable may have no jamaat at all (2026-09-27, CJAS). */}
                      {(r.begins && r.begins[k]) || '—'}
                      <div className="field note" style={{ fontSize: 12 }}>jamaat {(r.jamaat && r.jamaat[k]) || '—'}</div></td>
                  ))}
                  <td>{over
                    ? <TwoStep label="Fjern" busyLabel="Fjerner …" ariaLabel={`Fjern overstyringen for ${shortDate(r.date)}`}
                        confirmLabel={`Klikk igjen: ${shortDate(r.date)} får årstabellens tider i appen og på skjermene`} onConfirm={async () => {
                        const res = await send('/prayer-times/override/' + encodeURIComponent(r.date), { method: 'DELETE' });
                        sayOv(res.ok ? `Overstyringen for ${shortDate(r.date)} er fjernet — årstabellens tider gjelder igjen.` : errText(res.body), res.ok ? 'ok' : 'err');
                        await reload();
                      }} />
                    : <Btn ghost onClick={() => setEditing({ date: r.date, seq: Date.now() })} aria-label={`Endre ${shortDate(r.date)}`}>Endre</Btn>}</td>
                </tr>
              );
            }) : <tr><td colSpan={7}>Ingen bønnetider for denne uken. Last opp årstabellen over.</td></tr>}
          </tbody>
        </table>
      </Card>
      {/* Jamaat times as periods, own and IRN's (2026-10-15); a change reloads the week above. */}
      <JamaatCard onChanged={reload} />
      <OverrideCard rows={rows} editing={editing} onNew={() => setEditing({ date: '', seq: Date.now() })} onClose={() => setEditing(null)}
                    onSaved={reload} note={ovNote} say={sayOv} />
      {can('prayer.read') && <HijriCard />}
    </>
  );
}

/* ---------------- the year: upload, review, import (CA-20) ---------------- */

const rowLabel = (row: number) => row === 0 ? 'Hele filen' : row === 1 ? 'Rad 1 (overskriftene)' : 'Rad ' + row;

function Capped<T>({ items, max, render }: { items: T[]; max: number; render: (x: T, i: number) => React.ReactNode }) {
  return <ul>{items.slice(0, max).map((x, i) => <li key={i}>{render(x, i)}</li>)}{items.length > max && <li>… og {items.length - max} til.</li>}</ul>;
}

function TimetableCard({ data, onImported, sourceOn, hasSource = false, adoption = null }: {
  data: Body; onImported: () => void; sourceOn: boolean; hasSource?: boolean; adoption?: AdoptionState | null }) {
  const years: number[] = (data.years || []).map((y: Body) => Number(y.y)).filter(Boolean).sort();
  const nextYear = (years.length ? years[years.length - 1] : new Date().getFullYear()) + 1;
  const [file, setFile] = useState<File | null>(null);
  const [pending, setPending] = useState<Body | null>(null);
  const [review, setReview] = useState<Body | null>(null);
  const [keep, setKeep] = useState(true);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const input = useRef<HTMLInputElement>(null);
  useDirty('timetable', Boolean(review && review.ok));

  const reset = () => { setFile(null); setPending(null); setReview(null); setAck(false); setKeep(true); if (input.current) input.current.value = ''; };

  async function preview(f: File) {
    say(''); setReview(null); setFile(f);
    let body: Body;
    try { body = await readUpload(f); } catch (e) { reset(); return say((e as Error).message, 'err'); }
    setPending(body);
    const r = await post('/timetable/preview', body);
    if (!r.ok && !(r.body && r.body.reason === 'errors')) { reset(); return say(r.body.message || errText(r.body), 'err'); }
    setReview(r.body);
    say(r.body.ok ? '' : 'Filen har feil og kan ikke importeres slik den er.', r.body.ok ? '' : 'err');
  }

  async function commit() {
    if (!pending || !review || !review.ok) return;
    if ((review.suspectJumps || []).length && !ack) return say('Se over dagene med store hopp, og huk av for at tidene stemmer, før du importerer.', 'err');
    setBusy(true); say('Importerer …');
    const r = await post('/timetable/commit', { ...pending, keepOverrides: keep, acknowledgeSuspectJumps: ack });
    setBusy(false);
    if (!r.ok) {
      const b = r.body || {};
      return say(b.message || (Array.isArray(b.errors) ? b.errors.slice(0, 3).map((e: Body) => rowLabel(e.row) + ': ' + e.message).join(' · ') : errText(b)), 'err');
    }
    const b = r.body; const kept = (b.keptOverrideDates || []).length;
    say(`Importert: ${b.rows} dager for ${b.year}.` + (kept ? ` ${kept} dag${kept === 1 ? '' : 'er'} med egne endringer ble beholdt.` : '')
      + (b.runway ? ' ' + (b.runway.title_no || `${b.runway.days} dager med bønnetider igjen.`) : '')
      + ' Appen, skjermene og nettsiden bruker tabellen fra nå.', 'ok');
    reset(); onImported();
  }

  const rw = data.runway;
  const runway = rw ? [
    rw.title_no || (rw.days + ' dager med bønnetider igjen'), rw.body_no || null,
    (data.years || []).length ? 'Lastet opp: ' + data.years.map((y: Body) => `${y.y} (${y.n} dager)`).join(' · ') : null,
    data.lastImport ? 'Sist importert: ' + data.lastImport.filename + (data.lastImport.importedBy ? ' av ' + data.lastImport.importedBy : '') : 'Ingen årstabell er lastet opp.',
    // The seed says in words that it is generated demo data. Carried through:
    // plausible prayer times from a script must never pass for the mosque's.
    data.lastImport && data.lastImport.notes ? data.lastImport.notes : null,
  ].filter(Boolean).join(' · ') : '';

  const b = review;
  return (
    <Card title={years.length ? 'Årstabell ' + years.join(' og ') : 'Årstabell'}
          hint="Hele året legges inn én gang — 365 rader. Systemet viser dagens rad automatisk i appen, på skjermene og på nettsiden. Ingen utregning, ingen manuell oppdatering.">
      <CardBody>
        {sourceOn ? <p className="note tt-source-note">{sourceNote(hasSource, adoption)}</p> : <DropZone title="Slipp Excel- eller CSV-filen her, eller klikk for å velge fil" accept=".xlsx,.csv,.txt" onFile={preview} inputRef={input}>
          Last ned malen først · kolonner: dato, fajr, soloppgang, dhuhr, asr, maghrib og isha (når bønnen begynner), og jamaat for hver hvis moskeen har det (kan stå tomme) · månedsfilen fra Bønnetid Oslo kan lastes opp som den er
        </DropZone>}
        {file && !b && <div className="ttrev">Leser {file.name} …</div>}
        {b && file && (
          <div className="ttrev">
            {b.ok ? <>
              <p className="sum">{file.name}: {b.counts.total} dager{(b.stats && b.stats.years || []).length ? ' i ' + b.stats.years.join(', ') : ''} · {b.summary_no}</p>
              <p style={{ margin: '2px 0 0' }}>Ingenting er lagret ennå. Les gjennom, og klikk «Importer» når alt stemmer.</p>
            </> : <>
              <p className="sum bad">{b.message_no || 'Filen kan ikke importeres.'}</p>
              {(b.errors || []).length > 0 && <><h4>Feil som må rettes i filen (radnummer som i regnearket)</h4>
                <Capped items={b.errors} max={15} render={(e: Body) => <><span className="bad">{rowLabel(e.row)}:</span> {e.message}</>} /></>}
              <p style={{ margin: '6px 0 0' }}>Rett feilene i regnearket, lagre, og last opp filen på nytt. Ingenting er lagret.</p>
            </>}
            {(b.confirmations || []).map((c: Body) => c.id === 'overrides_lost' ? (
              <fieldset key={c.id}><legend>Dager med egne endringer</legend>
                <p style={{ margin: '0 0 4px' }}>{c.message_no}</p>
                <p className="note" style={{ margin: '0 0 4px' }}>{c.dates.map(longDate).join(', ')}</p>
                <label><input type="radio" name="tt-keep" checked={keep} onChange={() => setKeep(true)} /> Behold endringene (anbefalt) — de dagene beholder tidene som ble satt for hånd</label>
                <label><input type="radio" name="tt-keep" checked={!keep} onChange={() => setKeep(false)} /> Overskriv med filen — endringene på de dagene slettes</label>
              </fieldset>
            ) : <p key={c.id} className="warnc" style={{ margin: '8px 0 0' }}><b>Merk:</b> {c.message_no}</p>)}
            {(b.suspectJumps || []).length > 0 && <>
              <h4 className="warnc">Tider som flytter seg uvanlig mye fra dagen før</h4>
              <p style={{ margin: 0 }}>{b.suspect_no || ''} Det kan være riktig (Eid, sommertid), eller en skrivefeil eller byttede kolonner.</p>
              <Capped items={b.suspectJumps} max={10} render={(j: Body) => `${longDate(j.date)}: ${j.reason_no}`} />
              {b.ok && <label id="tt-ack-wrap"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Jeg har sett over disse dagene, og tidene stemmer.</label>}
            </>}
            {(b.warnings || []).length > 0 && <>
              <h4>Merknader ({b.warnings.length}) — filen kan importeres, men se over</h4>
              <Capped items={b.warnings} max={10} render={(w: Body) => (w.row ? `${rowLabel(w.row)}: ` : '') + w.message} />
            </>}
          </div>
        )}
        {!sourceOn && b && <div className="ib-acts" style={{ marginTop: 12 }}>
          {b.ok && <TwoStep ghost={false} label="Importer" busyLabel="Importerer …" disabled={busy}
            confirmLabel={`Klikk igjen: ${b.counts.total} dager erstatter tidene i appen, på skjermene og nettsiden`} onConfirm={commit} />}
          <Btn ghost onClick={() => { reset(); say('Avbrutt. Ingenting er lagret.'); }}>Avbryt</Btn>
        </div>}
        <Msg text={msg.text} tone={msg.tone} />
        <div className="row" style={{ marginTop: 14 }}>
          {!sourceOn && <Btn onClick={() => input.current?.click()}>Last opp {nextYear}</Btn>}
          <Btn ghost onClick={() => {
            const name = `bonnetider-mal-${nextYear}.csv`;
            saveBlob(new Blob([templateCsv(nextYear)], { type: 'text/csv;charset=utf-8' }), name);
            say(`Malen er lastet ned (${name}): én rad per dag i ${nextYear}, med dato og ukedag fylt ut. Fyll inn tidene som TT:MM (24 timer) og ${templateWhere(sourceOn, hasSource)} — som .xlsx eller .csv. Rader uten tider hoppes over.`, 'ok');
          }}>Last ned mal</Btn>
        </div>
        {runway && <span className={'field note' + (rw.level === 'ok' ? '' : ' warn')} style={{ marginTop: 10, display: 'block' }}>{runway}</span>}
      </CardBody>
    </Card>
  );
}

/* ---------------- overrides: a day or a range, preview first (CA-22) ---------------- */

const OV_REASONS: [string, string][] = [['eid', 'Eid'], ['ramadan', 'Ramadan'], ['closure', 'Stengt'], ['funeral', 'Janazah / begravelse'],
  ['maintenance', 'Vedlikehold'], ['weather', 'Vær'], ['other', 'Annet']];

/**
 * Owner 2026-10-23: the form is a popup. «+ Ny overstyring» opens it empty (editing.date ''), «Endre» on a day of the
 * week opens it with that day; the card on the page keeps the explanation and the last result.
 */
function OverrideCard({ rows, editing, onNew, onClose, onSaved, note, say }: {
  rows: Body[]; editing: { date: string; seq: number } | null; onNew: () => void; onClose: () => void; onSaved: () => void;
  note: { text: string; tone: any }; say: (t: string, tone?: any) => void;
}) {
  const blank = { from: '', to: '', reason: '', closed: false, note: '', times: {} as Record<string, string> };
  const [f, setF] = useState(blank);
  const [orig, setOrig] = useState<{ times: Record<string, string>; note: string }>({ times: {}, note: '' });
  const [saveLabel, setSaveLabel] = useState<string | null>(null);   // null = no valid preview
  const [invalid, setInvalid] = useState<string[]>([]);
  const [start, setStart] = useState('');
  useDirty('override', Boolean(saveLabel));

  useEffect(() => {
    if (!editing) return;
    const row = rows.find((x) => x.date === editing.date) || {};
    const times: Record<string, string> = {};
    for (const p of PRAYERS) times[p] = (row.jamaat && row.jamaat[p]) || '';
    // The note already on the day, and only the Norwegian master (fault 111):
    // a translation loaded here and saved back would overwrite the mosque's
    // own words with a machine's.
    const n = (row.overrideNoteLanguage === 'no' && row.overrideNote) || '';
    const first = { from: editing.date, to: editing.date, reason: '', closed: false, note: n, times };
    setF(first); setStart(JSON.stringify(first));
    setOrig({ times, note: n });
    setSaveLabel(null); setInvalid([]); say('');
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (patch: Partial<typeof f>) => { setF((x) => ({ ...x, ...patch })); setSaveLabel(null); setInvalid([]); };
  const body = () => {
    const patch: Body = {};
    for (const p of PRAYERS) { const v = f.times[p]; if (v && v !== orig.times[p]) patch[p + '_jamaat'] = v; }
    if (f.closed) patch.is_closed = true;
    if (f.note.trim() !== orig.note) patch.note_no = f.note.trim();
    return { from: f.from, to: f.to || f.from, reason: f.reason, patch };
  };
  // `before_begins`: a jamaat set before its prayer has begun — said per day,
  // with a Norwegian date, and the field marked. `after_window` (audit F-01): one
  // at or after its prayer's time is out (Fajr at sunrise …), said the same way.
  const refused = (b: Body) => {
    const errs: Body[] = Array.isArray(b && b.errors) ? b.errors : [];
    const early = errs.filter((e) => e && (e.code === 'before_begins' || e.code === 'after_window'));
    const lead = early.some((e) => e.code === 'after_window')
      ? 'Ikke lagret: jamaat må ligge innenfor bønnetiden. ' : 'Ikke lagret: jamaat kan ikke være før bønnetiden begynner. ';
    setInvalid(early.map((e) => String(e.field || '').replace('_jamaat', '')));
    say(early.length ? lead + early.slice(0, 4).map((e) =>
      (e.date ? shortDate(e.date) + ': ' : '') + String(e.message || '').replace(/^\d{4}-\d{2}-\d{2}: /, '')).join(' · ')
      + (early.length > 4 ? ` · og ${early.length - 4} dager til.` : '') : errText(b), 'err');
  };

  const hint = 'Én dag eller en periode — for eksempel Ramadan. Årstabellen endres ikke, og overstyringen overlever en ny import. Forhåndsvis først: ingenting lagres før du trykker Lagre.';
  return (
    <div className="card" id="ov-card">
      <h3>Overstyr bønnetider</h3>
      <p className="hint">{hint}</p>
      <CardBody>
        <div className="row"><Btn onClick={onNew}>+ Ny overstyring</Btn></div>
        {!editing && <Msg text={note.text} tone={note.tone} />}
      </CardBody>
      <FormDialog open={Boolean(editing)} title={editing && editing.date ? `Overstyr ${shortDate(editing.date)}` : 'Ny overstyring'} hint={hint}
                  dirty={JSON.stringify(f) !== start} onClose={onClose}>
        <div className="row">
          <Field label="Fra" className="field half">{(id) => <input id={id} type="date" value={f.from} onChange={(e) => change({ from: e.target.value })} />}</Field>
          <Field label="Til" className="field half">{(id) => <input id={id} type="date" value={f.to} onChange={(e) => change({ to: e.target.value })} />}</Field>
          <Field label="Årsak" required className="field half" note="Årsaken vises for medlemmene i appen og på skjermene, på deres språk.">
            {(id, d) => <select id={id} aria-describedby={d} aria-required="true" aria-invalid={invalid.includes('reason') || undefined} value={f.reason} onChange={(e) => change({ reason: e.target.value })}>
              <option value="">Velg årsak …</option>
              {OV_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
          </Field>
        </div>
        <div className="row">
          {PRAYERS.map((p) => (
            <Field key={p} label={cap(p) + ' jamaat'} style={{ maxWidth: 120 }}>
              {(id) => <input id={id} type="time" value={f.times[p] || ''} aria-invalid={invalid.includes(p) || undefined}
                              onChange={(e) => change({ times: { ...f.times, [p]: e.target.value } })} />}
            </Field>
          ))}
          <div className="field" style={{ maxWidth: 140 }}><label><input type="checkbox" checked={f.closed} onChange={(e) => change({ closed: e.target.checked })} /> Stengt</label></div>
        </div>
        {/* FAULT 110: the sentence a reason code cannot carry. 200 is core's limit. */}
        <div className="row">
          <Field label="Notat (valgfritt)" style={{ flex: 1 }} note="Vises for medlemmene sammen med årsaken, og oversettes til engelsk og urdu. Maks 200 tegn.">
            {(id, d) => <input id={id} type="text" maxLength={200} aria-describedby={d} placeholder="F.eks. Stengt pga. vannlekkasje i kjelleren"
                               value={f.note} onChange={(e) => change({ note: e.target.value })} />}
          </Field>
        </div>
        <div className="row">
          <Btn ghost onClick={async () => {
            const b = body();
            // A note ALONE is a real override (core says so), so it counts here.
            if (!Object.keys(b.patch).length) return say('Endre minst ett klokkeslett, huk av Stengt, eller skriv et notat.', 'err');
            if (!b.reason) { setInvalid(['reason']); return say('Velg årsak — den vises for medlemmene.', 'err'); }
            const r = await post('/prayer-times/overrides/bulk', b);
            if (!r.ok) { refused(r.body); setSaveLabel(null); return; }
            const c: Body[] = r.body.conflicts || [];
            say(r.body.message_no + (c.length ? ' ' + c.map((x) => `${shortDate(x.date)}: ${x.fields.map((y: string) => y.replace('_jamaat', '')).join(', ')} (${x.existingReason || 'uten årsak'})`).join(' · ') : ''), c.length ? 'warn' : 'ok');
            setSaveLabel(c.length ? `Lagre og overskriv ${c.length}` : `Lagre ${r.body.days} ${r.body.days === 1 ? 'dag' : 'dager'}`);
          }}>Forhåndsvis</Btn>
          <Btn disabled={!saveLabel} onClick={async () => {
            setSaveLabel(null);
            const r = await post('/prayer-times/overrides/bulk', { ...body(), confirm: true });
            if (r.ok) { say(r.body.message_no, 'ok'); setF(blank); setOrig({ times: {}, note: '' }); onSaved(); onClose(); } else refused(r.body);
          }}>{saveLabel || 'Lagre'}</Btn>
        </div>
        <Msg text={note.text} tone={note.tone} />
      </FormDialog>
    </div>
  );
}

/* ---------------- the Hijri date (CA-21) ---------------- */

function HijriCard() {
  const { data, reload } = useLoad<Body>('/hijri');
  const [from, setFrom] = useState(isoToday());
  const [offset, setOffset] = useState(1);
  const [reason, setReason] = useState('');
  const [bad, setBad] = useState(false);
  const [msg, say] = useMsg();
  // Owner 2026-10-23: a new adjustment is made in a popup; today's date and the list of adjustments stay on the card.
  const [adding, setAdding] = useState(false);
  const closeAdd = () => { setAdding(false); setReason(''); setBad(false); setFrom(isoToday()); setOffset(1); };
  const t = (data && data.today) || {};
  const list: Body[] = ((data && data.adjustments) || []).slice().sort((a: Body, b: Body) =>
    String(b.effective_from).localeCompare(String(a.effective_from)) || String(b.created_at).localeCompare(String(a.created_at)));
  const days = (n: number) => `${n > 0 ? '+' : '−'}${Math.abs(n)} ${Math.abs(n) === 1 ? 'dag' : 'dager'}`;
  return (
    <Card title="Hijri-dato" id="hj-card" hint="Den islamske datoen regnes ut automatisk. Når månen er observert og datoen må flyttes — typisk ved starten av Ramadan eller før Eid — justerer du den her med én eller to dager, fra en bestemt dato. Appen, skjermene og nettsiden følger justeringen.">
      <CardBody>
        <p style={{ margin: '0 0 12px' }}>I dag: <b>{t.formatted || '—'}</b>
          {t.adjusted ? <> · justert {days(data!.activeOffset)}{t.adjustmentReason ? <> — medlemmene ser: «{t.adjustmentReason}»</> : null}</> : ' · utregnet, ingen justering'}</p>
        <div className="row"><Btn onClick={() => { say(''); setAdding(true); }}>+ Ny justering</Btn></div>
        {!adding && <Msg text={msg.text} tone={msg.tone} />}
        <FormDialog open={adding} title="Juster Hijri-datoen" dirty={Boolean(reason.trim()) || offset !== 1} onClose={closeAdd}>
        <div className="row">
          <Field label="Gjelder fra og med" required className="field half" style={{ maxWidth: 200 }}>{(id) => <input id={id} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}</Field>
          <Field label="Justering" className="field half" style={{ maxWidth: 320 }}>{(id) => (
            <select id={id} value={offset} onChange={(e) => setOffset(Number(e.target.value))}>
              {[[-2, '−2 dager'], [-1, '−1 dag'], [0, 'Ingen justering (tilbake til utregnet dato)'], [1, '+1 dag'], [2, '+2 dager']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>)}</Field>
        </div>
        <Field label="Årsak" required noteId="hj-reason-warn">{(id) => (
          <input id={id} autoComplete="off" aria-describedby="hj-reason-warn" aria-invalid={bad || undefined} value={reason}
                 placeholder="F.eks. Ramadan begynner — månen observert 17.02" onChange={(e) => { setReason(e.target.value); setBad(false); }} />)}</Field>
        <div className="warnbox" id="hj-reason-warn"><b>Årsaken vises for medlemmene.</b> Den står i appen ved Hijri-datoen, oversatt til engelsk og urdu. Skriv den slik menigheten skal lese den — ingen interne notater eller navn.</div>
        <div className="row" style={{ marginTop: 12 }}><Btn onClick={async () => {
          if (reason.trim().length < 3) { setBad(true); return say('Skriv årsaken — den vises for medlemmene. For eksempel «Ramadan begynner — månen observert 17.02».', 'err'); }
          const r = await post('/hijri', { effectiveFrom: from, offsetDays: offset, reason: reason.trim() });
          if (!r.ok) return say(errText(r.body), 'err');
          say(`Lagret. Hijri-datoen i dag er ${(r.body.today && r.body.today.formatted) || '—'}. Årsaken oversettes og vises for medlemmene i appen.`, 'ok');
          closeAdd(); reload();
        }}>Lagre justering</Btn></div>
        <Msg text={msg.text} tone={msg.tone} />
        </FormDialog>
        {list.length > 0 && (
          <table className="t" style={{ marginTop: 12 }}>
            <thead><tr><th scope="col">Fra og med</th><th scope="col">Justering</th><th scope="col">Årsak (vises for medlemmene)</th><th scope="col">Lagret</th></tr></thead>
            <tbody>{list.slice(0, 8).map((a, i) => <tr key={i}><td>{longDate(a.effective_from)}</td>
              <td>{a.offset_days ? days(a.offset_days) : 'ingen (utregnet dato)'}</td><td>{a.reason_no || ''}</td><td>{osloTime(a.created_at)}</td></tr>)}</tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
